import React, { useState } from "react";
import { useLingui } from "@lingui/react";
import { t } from "@lingui/core/macro";
import { setHours, setMilliseconds, setMinutes, setSeconds } from "date-fns";
import { OneTimeSlot } from "../../api/scheduleApi.ts";
import ActionButton from "../ActionButton.tsx";
import Container from "../Container.tsx";
import ContainerHeader from "../ContainerHeader.tsx";
import FormField from "../FormField.tsx"; // Import the new FormField component
import TimeInputField from "../TimeInputField.tsx";
import DateInputField from "../DateInputField.tsx";
import { LocationWithName } from "../../api/locationApi.ts";

interface OneTimeSlotsFormProps {
  onAddOneTimeSlot: (oneTimeSlot: OneTimeSlot) => void;
  existingOneTimeSlots: OneTimeSlot[] | undefined;
  isLoading: boolean;
  onSelectLocation: (location: LocationWithName) => void;
  selectedLocation: LocationWithName;
  locations: LocationWithName[];
}

const generateTimeOptions = (limit: number) => {
  return Array.from({ length: limit }, (_, i) => String(i).padStart(2, "0"));
};

const hours = generateTimeOptions(24);
const minutes = generateTimeOptions(60);

const OneTimeSlotsForm: React.FC<OneTimeSlotsFormProps> = ({
  onAddOneTimeSlot,
  isLoading,
  onSelectLocation,
  selectedLocation,
  locations,
}) => {
  const { i18n } = useLingui();
  const [day, setDay] = useState<Date>(new Date());
  const [startHour, setStartHour] = useState("09");
  const [startMinute, setStartMinute] = useState("00");
  const [endHour, setEndHour] = useState("17");
  const [endMinute, setEndMinute] = useState("00");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const startTime = parseInt(startHour) * 60 + parseInt(startMinute);
    const endTime = parseInt(endHour) * 60 + parseInt(endMinute);

    if (endTime <= startTime) {
      setError(i18n._(t`End time must be after start time.`));
      return;
    }

    const from = setMilliseconds(
      setSeconds(
        setMinutes(setHours(day, parseInt(startHour)), parseInt(startMinute)),
        0,
      ),
      0,
    );
    const to = setMilliseconds(
      setSeconds(
        setMinutes(setHours(day, parseInt(endHour)), parseInt(endMinute)),
        0,
      ),
      0,
    );
    const newOneTimeSlots: OneTimeSlot = {
      id: undefined,
      locationId: selectedLocation.id,
      from,
      to,
    };
    onAddOneTimeSlot(newOneTimeSlots);
    setDay(new Date());
    setStartHour("09");
    setStartMinute("00");
    setEndHour("17");
    setEndMinute("00");
  };

  const handleSetLocation = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onSelectLocation(
      locations.filter(({ name }) => e.target.value === name)[0],
    );
  };

  return (
    <Container className={"mb-4"}>
      <ContainerHeader>{i18n._(t`Add New One Time Slot`)}</ContainerHeader>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Location selector - full width on its own line */}
        <FormField htmlFor="location" label={i18n._(t`Location`)}>
          <select
            id="location"
            name="location"
            value={selectedLocation?.name || ""}
            onChange={handleSetLocation}
            className="focus:border-soluud-primary focus:ring-soluud-primary/30 block w-full rounded-lg border border-gray-300 py-2 pr-10 pl-3 text-base transition-colors duration-150 focus:ring-2 focus:outline-none sm:text-sm"
          >
            {locations.map((loc) => (
              <option key={loc.id} value={loc.name}>
                {loc.name}
              </option>
            ))}
          </select>
        </FormField>

        {/* Day, start time, and end time - responsive layout */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <FormField htmlFor="day" label={i18n._(t`Day`)}>
            <DateInputField
              id="day"
              value={day}
              onChange={(e) => setDay(new Date(e.target.value))}
              label={""}
            />
          </FormField>

          <FormField htmlFor="startHour" label={i18n._(t`Start Time`)}>
            <div className="flex gap-2">
              <TimeInputField
                id="startHour"
                value={startHour}
                onChange={(e) => setStartHour(e.target.value)}
                options={hours}
              />
              <TimeInputField
                id="startMinute"
                value={startMinute}
                onChange={(e) => setStartMinute(e.target.value)}
                options={minutes}
              />
            </div>
          </FormField>

          <FormField htmlFor="endHour" label={i18n._(t`End Time`)}>
            <div className="flex gap-2">
              <TimeInputField
                id="endHour"
                value={endHour}
                onChange={(e) => setEndHour(e.target.value)}
                options={hours}
              />
              <TimeInputField
                id="endMinute"
                value={endMinute}
                onChange={(e) => setEndMinute(e.target.value)}
                options={minutes}
              />
            </div>
          </FormField>
        </div>

        {/* Submit button and error message */}
        <div>
          {error && <p className="mb-2 text-sm text-red-500">{error}</p>}
          <ActionButton
            type="submit"
            disabled={isLoading}
            isLoading={isLoading}
          >
            {i18n._(t`Add One Time Slot`)}
          </ActionButton>
        </div>
      </form>
    </Container>
  );
};

export default OneTimeSlotsForm;
